class PickupAvailability {
  constructor(element) {
    this.element = element;
    this.drawer = null;
    this.activeButton = null;
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onKeyDown = this.handleKeyDown.bind(this);
    this.onVariantChange = this.handleVariantChange.bind(this);

    this.errorHtml = element.querySelector('template')?.content.firstElementChild.cloneNode(true);
    this.fetchAvailability(element.dataset.variantId);
    document.addEventListener('variant:change', this.onVariantChange);
  }

  fetchAvailability(variantId) {
    if (!variantId) return;

    this.element.dataset.variantId = variantId;
    this.closeDrawer();

    const rootUrl = this.getRootUrl();
    const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;

    fetch(variantSectionUrl)
      .then(response => response.text())
      .then(text => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const section = html.querySelector('.shopify-section');
        this.render(section);
      })
      .catch(() => this.renderError());
  }

  getRootUrl() {
    const rootUrl = this.element.dataset.rootUrl || window.Shopify?.routes?.root || '/';
    return rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
  }

  handleVariantChange(event) {
    const eventContainer = event.detail?.container;
    if (eventContainer && !eventContainer.contains(this.element)) return;

    const variant = event.detail?.variant;

    if (variant?.available) {
      this.fetchAvailability(variant.id);
      return;
    }

    this.clear();
  }

  render(section) {
    const preview = section?.querySelector('.pickup-availability-preview');
    const drawer = section?.querySelector('.pickup-availability-drawer');

    if (!preview || !drawer) {
      this.clear();
      return;
    }

    this.removeDrawer();
    this.element.innerHTML = preview.outerHTML;
    document.body.appendChild(drawer);
    this.drawer = drawer;
    this.element.setAttribute('data-available', 'true');

    this.setProductTitle();
    this.bindPreviewButton();
    this.bindCloseButton();
  }

  renderError() {
    if (!this.errorHtml) {
      this.clear();
      return;
    }

    this.removeDrawer();
    this.element.innerHTML = '';
    this.element.appendChild(this.errorHtml.cloneNode(true));
    this.element.removeAttribute('data-available');

    this.element.querySelector('button')?.addEventListener('click', () => {
      this.fetchAvailability(this.element.dataset.variantId);
    });
  }

  clear() {
    this.closeDrawer();
    this.removeDrawer();
    this.element.innerHTML = '';
    this.element.removeAttribute('data-available');
  }

  removeDrawer() {
    document.querySelectorAll('.pickup-availability-drawer').forEach(drawer => drawer.remove());
    this.drawer = null;
  }

  setProductTitle() {
    const productTitle = this.element.dataset.productTitle;
    const titleElement = this.drawer?.querySelector('[data-pickup-availability-product-title]');

    if (titleElement && productTitle) {
      titleElement.textContent = productTitle;
    }
  }

  bindPreviewButton() {
    const button = this.element.querySelector('.pickup-availability-preview__button');
    if (!button) return;

    this.activeButton = button;
    button.addEventListener('click', () => this.openDrawer());
  }

  bindCloseButton() {
    this.drawer?.querySelector('.pickup-availability-drawer__close')?.addEventListener('click', () => {
      this.closeDrawer();
      this.activeButton?.focus();
    });
  }

  openDrawer() {
    if (!this.drawer || !this.activeButton) return;

    this.activeButton.setAttribute('aria-expanded', 'true');
    document.body.classList.add('pickup-availability-drawer-is-open');
    document.body.style.overflow = 'hidden';
    document.body.style.height = 'auto';
    this.drawer.setAttribute('open', '');
    this.focusDrawer();

    document.addEventListener('click', this.onBodyClick);
    document.addEventListener('keydown', this.onKeyDown);
  }

  closeDrawer() {
    this.activeButton?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('pickup-availability-drawer-is-open');
    document.body.style.overflow = 'unset';
    document.body.style.height = 'unset';

    if (this.drawer) {
      this.drawer.removeAttribute('open');
    }

    document.removeEventListener('click', this.onBodyClick);
    document.removeEventListener('keydown', this.onKeyDown);
  }

  handleBodyClick(event) {
    if (
      this.drawer?.contains(event.target) ||
      this.activeButton?.contains(event.target)
    ) {
      return;
    }

    this.closeDrawer();
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      this.closeDrawer();
      this.activeButton?.focus();
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  focusDrawer() {
    const focusableElements = this.getFocusableElements();
    const focusTarget = focusableElements[0] || this.drawer;
    focusTarget?.focus();
  }

  trapFocus(event) {
    const focusableElements = this.getFocusableElements();
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    if (!firstFocusable || !lastFocusable) return;

    if (event.shiftKey && document.activeElement === firstFocusable) {
      event.preventDefault();
      lastFocusable.focus();
    } else if (!event.shiftKey && document.activeElement === lastFocusable) {
      event.preventDefault();
      firstFocusable.focus();
    }
  }

  getFocusableElements() {
    if (!this.drawer) return [];

    return Array.from(
      this.drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(element => element.offsetParent !== null);
  }
}

function initPickupAvailability(container = document) {
  container.querySelectorAll('.product__pickup-availabilities').forEach(element => {
    if (element.dataset.pickupAvailabilityInitialized === 'true') return;

    element.dataset.pickupAvailabilityInitialized = 'true';
    new PickupAvailability(element);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initPickupAvailability());
} else {
  initPickupAvailability();
}

document.addEventListener('shopify:section:load', event => {
  initPickupAvailability(event.target);
});
